import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { subscribeToForegroundPush } from '../lib/push-driver';
import { trpc } from '../lib/trpc';

/**
 * FCM delivers to the window, not the service worker, whenever the tab is
 * focused — and the browser suppresses the OS toast in that case. Without this
 * the most attentive user gets the *least* feedback: nothing at all appears
 * until something else refetches.
 *
 * Renders an in-app toast instead of an OS one, which is the right surface
 * anyway when the user is already looking at the app.
 */
export function useForegroundPush() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  useEffect(() => {
    return subscribeToForegroundPush((payload) => {
      const data = payload.data ?? {};
      if (!data.notificationId) return;

      // The socket usually beats push here, but not when the DO connection has
      // dropped — so refresh on both rather than trusting either alone.
      void utils.notifications.list.invalidate();
      void utils.notifications.unreadCount.invalidate();

      toast(data.label || 'New notification', {
        description: data.content,
        // Built server-side from the shared route map; absent for targets with
        // no view of their own, in which case there is nothing to open.
        action: data.path
          ? { label: 'View', onClick: () => navigate(data.path as string) }
          : undefined,
      });
    });
  }, [navigate, utils]);
}
